import * as T from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { TOWN_BOXES, TOWN_LENGTH, mulberry32, type ModeId } from '@shooting-range/shared';

export const material = (color: number, metalness = 0, roughness = 0.85): T.MeshStandardMaterial => new T.MeshStandardMaterial({ color, metalness, roughness });
export function box(parent: T.Object3D, size: [number, number, number], position: [number, number, number], mat: T.Material): T.Mesh {
  const mesh = new T.Mesh(new T.BoxGeometry(...size), mat);
  mesh.position.set(...position); mesh.castShadow = mesh.receiveShadow = true; parent.add(mesh); return mesh;
}
export function cylinder(parent: T.Object3D, radius: number, height: number, position: [number, number, number], mat: T.Material, segments = 12): T.Mesh {
  const mesh = new T.Mesh(new T.CylinderGeometry(radius, radius, height, segments), mat);
  mesh.position.set(...position); mesh.castShadow = true; parent.add(mesh); return mesh;
}
export function label(text: string, color = '#d7e5bd', background = '#202824'): T.CanvasTexture {
  const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 128;
  const context = canvas.getContext('2d')!;
  context.fillStyle = background; context.fillRect(0, 0, 512, 128);
  context.strokeStyle = color; context.lineWidth = 2; context.strokeRect(6, 6, 500, 116);
  context.fillStyle = color; context.font = 'bold 57px sans-serif'; context.textAlign = 'center'; context.textBaseline = 'middle'; context.fillText(text, 256, 67, 470);
  const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace; return texture;
}
function sign(parent: T.Object3D, text: string, x: number, y: number, z: number, width = 5, rotation = 0): void {
  const mesh = new T.Mesh(new T.PlaneGeometry(width, width / 4), new T.MeshStandardMaterial({ map: label(text), roughness: 0.9, side: T.DoubleSide }));
  mesh.position.set(x, y, z); mesh.rotation.y = rotation; parent.add(mesh);
}
function surface(kind: 'road' | 'brick' | 'ground'): T.CanvasTexture {
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = 256;
  const c = canvas.getContext('2d')!; const random = mulberry32(5872);
  c.fillStyle = kind === 'road' ? '#474a44' : kind === 'brick' ? '#847869' : '#8b8e71'; c.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 9_000; i++) {
    c.fillStyle = `rgba(${random() > 0.5 ? '230,230,210' : '0,0,0'},${random() * 0.13})`;
    c.fillRect(random() * 256, random() * 256, 1 + random() * 2, 1 + random() * 2);
  }
  if (kind === 'brick') {
    c.strokeStyle = '#48443b'; c.lineWidth = 2;
    for (let y = 0; y < 256; y += 16) {
      c.beginPath(); c.moveTo(0, y); c.lineTo(256, y); c.stroke();
      for (let x = (y % 32 ? 16 : 0); x < 256; x += 32) { c.beginPath(); c.moveTo(x, y); c.lineTo(x, y + 16); c.stroke(); }
    }
  } else {
    c.strokeStyle = 'rgba(15,19,15,.22)'; c.lineWidth = 1;
    for (let i = 0; i < 12; i++) {
      let x = random() * 256, y = random() * 256; c.beginPath(); c.moveTo(x, y);
      for (let j = 0; j < 6; j++) { x += (random() - 0.5) * 28; y += random() * 12; c.lineTo(x, y); } c.stroke();
    }
  }
  const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace;
  texture.wrapS = texture.wrapT = T.RepeatWrapping; texture.repeat.set(kind === 'brick' ? 2 : 10, kind === 'brick' ? 2 : 70); texture.anisotropy = 4;
  return texture;
}

export interface Environment { group: T.Group; sun: T.DirectionalLight; dust: T.Points; color: number }
export function buildEnvironment(mode: ModeId): Environment {
  const group = new T.Group(); const town = mode === 'town'; const forest = mode === 'attack'; const range = mode === 'classic';
  const random = mulberry32(4242);
  const color = forest ? 0x616c62 : town ? 0xa89a7c : range ? 0x969b85 : 0xb5ae92;
  group.add(new T.HemisphereLight(forest ? 0xb7d7d1 : 0xf4e7c2, 0x343b30, 2));
  const sun = new T.DirectionalLight(0xffdab0, forest ? 2.5 : 3.4); sun.position.set(-32, 32, -68);
  sun.castShadow = true; sun.shadow.mapSize.set(1536, 1536); sun.shadow.bias = -0.0003; sun.shadow.normalBias = 0.08;
  Object.assign(sun.shadow.camera, { left: -36, right: 36, top: 42, bottom: -30, near: 1, far: 140 });
  group.add(sun, sun.target);
  const groundMat = new T.MeshStandardMaterial({ color: forest ? 0x5a6654 : 0xb3b095, map: surface('ground'), roughness: 1 });
  const ground = new T.Mesh(new T.PlaneGeometry(500, 700), groundMat); ground.rotation.x = -Math.PI / 2; ground.position.set(0, -0.035, -140); ground.receiveShadow = true; group.add(ground);
  const mountains = material(forest ? 0x374c43 : 0x767d65);
  for (let i = 0; i < 35; i++) {
    const mountain = new T.Mesh(new T.ConeGeometry(22 + random() * 30, 20 + random() * 50, 5), mountains);
    mountain.position.set(-230 + i * 14, 0, -400 - random() * 40); mountain.rotation.y = random() * 6; group.add(mountain);
  }
  const sunDisk = new T.Mesh(new T.SphereGeometry(7, 24, 16), new T.MeshBasicMaterial({ color: 0xffe6b0, fog: false }));
  sunDisk.position.set(-140, 84, -390); group.add(sunDisk);
  if (town) buildTown(group);
  else if (range) buildKiln(group);
  else {
    const pad = material(forest ? 0x514f40 : 0x969b80);
    box(group, [18, 0.14, 7], [0, -0.04, 3], pad);
    sign(group, forest ? 'HOLD THE LINE' : 'SALT FLATS', 0, 3.4, 8, 7, Math.PI);
    for (const x of [-9, 9]) cylinder(group, 0.08, 4, [x, 2, 7], material(0x434d43, 0.6));
    if (forest) {
      for (const x of [-12, 12]) for (let z = 0; z > -60; z -= 12) {
        box(group, [2, 0.7, 2], [x, 0.35, z], material(0x696b4b));
        const lamp = new T.Mesh(new T.SphereGeometry(0.15, 8, 6), new T.MeshBasicMaterial({ color: 0xe1bd76 })); lamp.position.set(x, 1.05, z); group.add(lamp);
      }
    } else {
      for (let i = 0; i < 26; i++) {
        const rock = new T.Mesh(new T.DodecahedronGeometry(0.3 + random() * 1.2), material(0x807f68));
        rock.position.set((random() > 0.5 ? 1 : -1) * (18 + random() * 50), 0.2, -random() * 120); rock.scale.y = 0.55; group.add(rock);
      }
    }
  }
  // Instanced tree silhouettes keep the distant world inexpensive to draw.
  const count = town ? 90 : forest ? 260 : 90;
  const trunks = new T.InstancedMesh(new T.CylinderGeometry(0.17, 0.32, 6, 6), material(0x464739), count);
  const leaves = new T.InstancedMesh(new T.ConeGeometry(2.8, 9, 7), material(forest ? 0x263e32 : 0x45513b), count);
  const dummy = new T.Object3D();
  for (let i = 0; i < count; i++) {
    const side = random() > 0.5 ? 1 : -1; const x = side * ((town ? 30 : 19) + random() * 70), z = -random() * (town ? 350 : 190), size = 0.6 + random() * 1.4;
    dummy.position.set(x, size * 3, z); dummy.scale.setScalar(size); dummy.updateMatrix(); trunks.setMatrixAt(i, dummy.matrix);
    dummy.position.y = size * 7; dummy.updateMatrix(); leaves.setMatrixAt(i, dummy.matrix);
  }
  group.add(trunks, leaves);
  const dustPositions = new Float32Array(400 * 3);
  for (let i = 0; i < dustPositions.length; i += 3) { dustPositions[i] = (random() - 0.5) * 65; dustPositions[i + 1] = random() * 12; dustPositions[i + 2] = -random() * 100; }
  const dustGeo = new T.BufferGeometry(); dustGeo.setAttribute('position', new T.BufferAttribute(dustPositions, 3));
  const particleCanvas = document.createElement('canvas'); particleCanvas.width = particleCanvas.height = 32;
  const particleContext = particleCanvas.getContext('2d')!;
  const gradient = particleContext.createRadialGradient(16, 16, 0, 16, 16, 16);
  gradient.addColorStop(0, '#fff'); gradient.addColorStop(0.3, '#ffffffbb'); gradient.addColorStop(1, '#ffffff00');
  particleContext.fillStyle = gradient; particleContext.fillRect(0, 0, 32, 32);
  const particleTexture = new T.CanvasTexture(particleCanvas);
  const dust = new T.Points(dustGeo, new T.PointsMaterial({ map: particleTexture, color: 0xffe4b2, size: 0.05, transparent: true, opacity: 0.4, depthWrite: false })); group.add(dust);
  batchStatic(group);
  return { group, sun, dust, color };
}

function buildKiln(group: T.Group): void {
  const concrete = material(0x727869), dark = material(0x343e39, 0.55), trim = material(0xc1ca82);
  box(group, [28, 0.1, 140], [0, 0, -60], concrete);
  for (const side of [-1, 1]) {
    box(group, [1.5, 7, 140], [side * 16, 3.5, -60], concrete);
    for (let z = 0; z > -130; z -= 12) box(group, [0.3, 7.4, 0.7], [side * 15.2, 3.7, z], dark);
  }
  box(group, [32, 9, 2], [0, 4.5, -128], concrete);
  for (let lane = 0; lane <= 8; lane++) {
    box(group, [0.1, 1.5, 3], [(lane - 4) * 1.5, 0.75, 3.5], dark);
    box(group, [0.12, 0.05, 3], [(lane - 4) * 1.5, 1.52, 3.5], trim);
  }
  box(group, [15, 0.25, 6], [0, 3.7, 3], dark);
  for (let lane = 0; lane < 8; lane++) sign(group, `0${lane + 1}`, (lane - 3.5) * 1.5, 2.8, 0.1, 0.7);
  for (let z = -25; z >= -100; z -= 25) {
    box(group, [27, 0.015, 0.07], [0, 0.06, z], trim);
    sign(group, `${-z} METERS`, -13.5, 1.8, z, 3);
    box(group, [30, 0.45, 1.4], [0, 6, z], dark);
  }
}

function buildTown(group: T.Group): void {
  const asphalt = new T.MeshStandardMaterial({ color: 0x6e716b, map: surface('road'), roughness: 0.97 });
  box(group, [26, 0.05, 370], [0, -0.01, -160], asphalt);
  const concrete = material(0xa0a08c), iron = material(0x333d36, 0.65, 0.45), yellow = material(0xb6ad6b);
  for (const side of [-1, 1]) {
    box(group, [1.1, 0.2, 365], [side * 12.25, 0.03, -160], concrete);
    box(group, [0.17, 0.25, 365], [side * 11.65, 0.08, -160], concrete);
  }
  for (let z = 4; z > -340; z -= 9) for (const x of [-0.18, 0.18]) box(group, [0.07, 0.02, 4], [x, 0.035, z], yellow);
  const brick = surface('brick');
  const buildings = [0xc5b091, 0x9ca28c, 0x98887c, 0xa5a89c, 0xb5a888].map(color => new T.MeshStandardMaterial({ color, map: brick, roughness: 0.95 }));
  const glass = material(0x1b302e, 0.45, 0.2), warm = new T.MeshStandardMaterial({ color: 0x8b7246, emissive: 0xffbf60, emissiveIntensity: 0.5, roughness: 0.4 });
  const shopNames = ['ASHFALL INN', 'GENERAL STORE', 'NORTH CLINIC', 'REPAIR & CO.', 'LAST STOP', 'POST OFFICE'];
  let buildingIndex = 0;
  for (const collider of TOWN_BOXES) {
    const { x, z, width, depth, height, kind } = collider;
    if (kind === 'car') { buildCar(group, x, z); continue; }
    if (kind === 'crate') {
      box(group, [width, height, depth], [x, height / 2, z], material(0x6e7351));
      for (const dz of [-0.8, 0.8]) box(group, [2.06, 1.84, 0.12], [x, 0.92, z + dz], iron);
      continue;
    }
    const i = buildingIndex++, side = Math.sign(x), face = side * 12.98;
    box(group, [width, height, depth], [x, height / 2, z], buildings[i % buildings.length]!);
    box(group, [width + 0.35, 0.3, depth + 0.35], [x, height + 0.08, z], concrete);
    box(group, [width, 0.4, depth + 0.12], [x, 3.3, z], concrete);
    for (let level = 4.8; level < height - 0.5; level += 2.8) for (let offset = -7; offset <= 7; offset += 3.5) {
      box(group, [0.14, 2.1, 1.65], [face, level, z + offset], concrete);
      box(group, [0.18, 1.8, 1.35], [face - side * 0.04, level, z + offset], (i + Math.round(offset) + level) % 5 < 1 ? warm : glass);
      box(group, [0.2, 0.055, 1.4], [face - side * 0.08, level, z + offset], iron);
      box(group, [0.2, 1.85, 0.06], [face - side * 0.08, level, z + offset], iron);
    }
    for (const offset of [-5.5, 5.5]) {
      box(group, [0.18, 2.1, 2.8], [face, 1.25, z + offset], glass);
      const awning = box(group, [1.4, 0.12, 3.6], [face - side * 0.6, 2.7, z + offset], material(i % 2 ? 0x665c42 : 0x4f6858)); awning.rotation.z = side * 0.15;
    }
    box(group, [0.2, 2.5, 1.4], [face, 1.25, z], iron);
    sign(group, shopNames[i % shopNames.length]!, face - side * 0.15, 3.1, z, 6, -side * Math.PI / 2);
    box(group, [2, 0.8, 2.5], [x, height + 0.65, z], iron);
  }
  for (let z = -6; z > -335; z -= 24) {
    for (const side of [-1, 1]) {
      cylinder(group, 0.065, 5.8, [side * 11.9, 2.9, z], iron, 8);
      box(group, [1.25, 0.08, 0.1], [side * 11.35, 5.75, z], iron);
      box(group, [0.6, 0.08, 0.24], [side * 10.9, 5.72, z], new T.MeshStandardMaterial({ color: 0xffd392, emissive: 0xffb056, emissiveIntensity: 3 }));
    }
    const curve = new T.QuadraticBezierCurve3(new T.Vector3(-13, 7, z), new T.Vector3(0, 4.5, z), new T.Vector3(13, 7, z));
    group.add(new T.Line(new T.BufferGeometry().setFromPoints(curve.getPoints(14)), new T.LineBasicMaterial({ color: 0x343c33 })));
  }
  for (let i = 0; i < 36; i++) {
    const z = -10 - i * 9; const side = i % 2 ? -1 : 1;
    box(group, [0.5, 0.03, 0.65], [side * (8.7 + i % 3), 0.05, z], material(0x9a9781)).rotation.y = i * 1.7;
  }
  for (const x of [-10, 10]) box(group, [0.3, 6.7, 0.3], [x, 3.35, 7], iron);
  box(group, [20.3, 0.3, 0.3], [0, 6.6, 7], iron);
  sign(group, 'ASHFALL / EVAC ROUTE', 0, 5.8, 7, 10, Math.PI);
  sign(group, 'EXTRACTION', 0, 4, -TOWN_LENGTH - 3, 9);
  const extractionMat = new T.MeshBasicMaterial({ color: 0xc5f58b, transparent: true, opacity: 0.3, side: T.DoubleSide, depthWrite: false });
  box(group, [23, 0.03, 7], [0, 0.06, -TOWN_LENGTH], extractionMat);
  for (const x of [-10, 10]) {
    cylinder(group, 0.3, 5, [x, 2.5, -TOWN_LENGTH], extractionMat);
    group.add(new T.PointLight(0xadeb67, 8, 12, 2).translateX(x).translateZ(-TOWN_LENGTH).translateY(1));
  }
}
function buildCar(group: T.Group, x: number, z: number): void {
  const paint = material(0x697a6c, 0.6, 0.5), black = material(0x1c2421), glass = material(0x293c36, 0.65, 0.15);
  box(group, [2.2, 0.75, 4.6], [x, 0.6, z], paint);
  box(group, [1.85, 0.65, 2.15], [x, 1.17, z + 0.1], glass);
  box(group, [1.95, 0.12, 2.2], [x, 1.44, z + 0.1], paint);
  for (const side of [-1, 1]) for (const dz of [-1.4, 1.4]) {
    const wheel = cylinder(group, 0.42, 0.2, [x + side * 1.03, 0.42, z + dz], black); wheel.rotation.z = Math.PI / 2;
  }
  for (const side of [-1, 1]) box(group, [0.42, 0.2, 0.05], [x + side * 0.7, 0.7, z - 2.32], material(0xc4b779));
}

export function disposeTree(root: T.Object3D): void {
  const geometries = new Set<T.BufferGeometry>(), materials = new Set<T.Material>(), textures = new Set<T.Texture>();
  root.traverse(object => {
    if (object instanceof T.DirectionalLight) object.shadow.map?.dispose();
    if (!(object instanceof T.Mesh || object instanceof T.Line || object instanceof T.Points)) return;
    geometries.add(object.geometry);
    for (const mat of Array.isArray(object.material) ? object.material : [object.material]) materials.add(mat);
  });
  for (const mat of materials) for (const value of Object.values(mat)) if (value instanceof T.Texture) textures.add(value);
  textures.forEach(texture => texture.dispose()); materials.forEach(mat => mat.dispose()); geometries.forEach(geometry => geometry.dispose());
}

export function batchStatic(group: T.Group): void {
  group.updateMatrixWorld(true);
  const batches = new Map<T.Material, T.Mesh[]>();
  for (const child of [...group.children]) {
    if (!(child instanceof T.Mesh) || child instanceof T.InstancedMesh || Array.isArray(child.material) || child.material.transparent) continue;
    const list = batches.get(child.material) ?? []; list.push(child); batches.set(child.material, list);
  }
  for (const [mat, meshes] of batches) {
    if (meshes.length < 2) continue;
    const transformed = meshes.map(mesh => (mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone()).applyMatrix4(mesh.matrix));
    const merged = mergeGeometries(transformed);
    transformed.forEach(geometry => geometry.dispose());
    if (!merged) continue;
    const originals = new Set(meshes.map(mesh => mesh.geometry));
    meshes.forEach(mesh => group.remove(mesh)); originals.forEach(geometry => geometry.dispose());
    const mesh = new T.Mesh(merged, mat); mesh.castShadow = meshes.some(m => m.castShadow); mesh.receiveShadow = true; group.add(mesh);
  }
}
